import { type ComponentProps, useMemo } from 'react'

import { type FormattedNumber as DefaultFormattedNumber, useLoading, VisuallyHidden } from '@subaio/components'
import { PlaceholderInternal } from '@subaio/components/src/Placeholder/PlaceholderInternal'
import { type FormatNumberOptions, formatNumber, formatNumberToParts } from '@subaio/utils'

import { Entry } from './Entry'
import { LiftedFraction } from './LiftedFraction'

const currencies: Record<string, FormatNumberOptions> = {
	EUR: { prefix: '€ ' },
}

type Props = ComponentProps<typeof DefaultFormattedNumber> & {
	value: number | undefined
	showCurrency?: boolean
	isFractionLifted?: boolean
	screenReaderValue?: string
}

const getCurrencyOptions = (currency?: string) => {
	if (!currency) return undefined

	return currencies[currency] ?? { prefix: `${currency} ` }
}

export const FormattedNumber = ({
	value,

	isFractionLifted = true,
	showCurrency = false,
	currency,
	// Currently unused, only used for currencies
	// style = 'currency',
	minimumFractionDigits = 2,

	placeholderLines,
	placeholderWidth,
	className,
	screenReaderValue,
}: Props) => {
	const isLoading = useLoading()
	const screenReaderFormatted = useMemo(
		() =>
			isLoading || value === undefined || Number.isNaN(value) || screenReaderValue
				? (screenReaderValue ?? '')
				: formatNumber(value, {
						precision: minimumFractionDigits,
						decimal: '.',
						group: ',',
						suffix: currency,
					}),
		[currency, isLoading, minimumFractionDigits, screenReaderValue, value],
	)
	const formattedParts = useMemo(
		() =>
			isLoading || value === undefined || Number.isNaN(value)
				? []
				: formatNumberToParts(value, {
						minus: '- ',
						precision: minimumFractionDigits,
						decimal: ',',
						group: '.',
						...getCurrencyOptions(currency),
					}),
		[isLoading, value, minimumFractionDigits, currency],
	)

	if (isLoading) {
		return <PlaceholderInternal lines={placeholderLines} width={placeholderWidth} className={className} />
	}

	return (
		<>
			<VisuallyHidden isInline>{screenReaderFormatted}</VisuallyHidden>
			<Entry className={className} aria-hidden>
				{formattedParts.map(({ type, value }, index) => {
					switch (type) {
						case 'fraction':
							return isFractionLifted ? <LiftedFraction key={index}>{value}</LiftedFraction> : value
						case 'prefix':
						case 'infix':
						case 'suffix':
							return showCurrency ? value : ''
						default:
							return value
					}
				})}
			</Entry>
		</>
	)
}
